import { lazy, type ComponentType } from "react";
import { Loader2 } from "lucide-react";

const RELOADED_KEY = "lazy-page-reloaded";

/**
 * A route page loaded on first use, so each page is its own chunk instead of
 * one bundle with the whole app. After a deploy an open tab may ask for a
 * chunk that no longer exists; then it reloads once to get the new build.
 */
export function lazyPage<T extends ComponentType<object>>(
  load: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    load().then(
      (module) => {
        sessionStorage.removeItem(RELOADED_KEY);
        return module;
      },
      (error: unknown) => {
        if (!sessionStorage.getItem(RELOADED_KEY)) {
          sessionStorage.setItem(RELOADED_KEY, "1");
          window.location.reload();
          return new Promise<never>(() => {});
        }
        throw error;
      },
    ),
  );
}

/** Shown while a page's chunk loads */
export const PageLoading = ({
  fullScreen = false,
}: {
  fullScreen?: boolean;
}) => (
  <div
    className={
      fullScreen
        ? "flex min-h-screen items-center justify-center bg-background"
        : "flex items-center justify-center py-24"
    }
  >
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);
